import {
  CondensedButton,
  Flex,
  Heading,
  IconDownloadLine,
  Spinner,
  Text,
} from "@instructure/ui";
import { useTranslation } from "react-i18next";
import { useEffect, useState } from "react";
import Skeleton from "react-loading-skeleton";
import RichTextEditor from "react-rte";
import {
  ERROR,
  INFO,
  useNotification,
} from "../../components/CustomHooks/NotificationProvider";
import CustomRTE, {
  EMPTY_RTE,
} from "../../components/CustomTextArea/CustomRTE";
import { ExternalService } from "../../services/ExternalService";
import { Files } from "../../services/FilesService";
import downloadFile from "../../utils/Dom";

const PublicQualitativeMeasure = ({ measure, tenantId }) => {
  const { t } = useTranslation();
  const showNotification = useNotification();
  const [evidence, setEvidence] = useState(null);
  const [instructions, setInstructions] = useState(null);
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    const getFileContent = async () => {
      try {
        const filePromises = [
          Files.getPublicFileContent(
            "response.html",
            "public-qualitativeEvidence",
            measure.ddl_id,
            tenantId
          ),
          Files.getPublicFileContent(
            "response.html",
            "public-qualitativeMeasure",
            measure.ddl_id,
            tenantId
          ),
        ];

        const [evidence, measureInstructions] = await Promise.all(filePromises);

        if (evidence.content === "") {
          setEvidence(
            "<p><em>No measure results or evidence have been submitted.</em></p>"
          );
        } else {
          setEvidence(evidence.content);
        }
        setInstructions(measureInstructions.content);
      } catch {
        showNotification(
          t("notifications.assessmentResults.getMeasureError"),
          ERROR
        );
      }
    };
    getFileContent();
  }, [measure.ddl_id, tenantId, showNotification]);

  const downloadMeasureEvidence = async () => {
    setDownloading(true);
    try {
      let paths = await ExternalService.reviewFiles(
        "qualitativeEvidence",
        measure.ddl_id
      );
      paths = paths.filter((file) => file.file_name !== "response.html");
      if (paths.length > 0) {
        paths.forEach((element) => {
          downloadFile(element.file_url, element.file_name);
        });
      } else {
        showNotification(
          t("results.noEvidenceForMeasure", { measureName: measure.name }),
          INFO
        );
      }
    } catch {
      showNotification(
        t("notifications.assessmentResults.downloadMeasureEvidenceError"),
        ERROR
      );
    } finally {
      setDownloading(false);
    }
  };

  return (
    <Flex wrap="wrap" gap="small">
      <Flex.Item size="100%">
        <Heading level="h3">{measure.name}</Heading>
      </Flex.Item>
      {evidence !== null && instructions !== null ? (
        <>
          {!!instructions && instructions !== EMPTY_RTE && (
            <Flex.Item size="100%">
              <RichTextEditor
                className="readOnlyClass"
                readOnly
                value={RichTextEditor.createValueFromString(
                  instructions,
                  "html"
                )}
              />
            </Flex.Item>
          )}
          <Flex.Item size="100%">
            <CustomRTE
              id={measure.ddl_id}
              label={<Text weight="bold">{t("results.measureEvidence")}</Text>}
              defaultValue={evidence}
              onChange={(value) => setEvidence(value)}
              readOnly
            />
          </Flex.Item>
          {measure.upload_evidence && (
            <Flex.Item shouldGrow textAlign="end">
              <CondensedButton
                renderIcon={
                  downloading ? (
                    <Spinner
                      renderTitle={t("global.pleaseWait")}
                      size="x-small"
                    />
                  ) : (
                    IconDownloadLine
                  )
                }
                disabled={downloading}
                onClick={downloadMeasureEvidence}
              >
                {t("notifications.assessmentResults.downloadMeasureEvidence")}
              </CondensedButton>
            </Flex.Item>
          )}
        </>
      ) : (
        <Flex.Item shouldGrow>
          <Skeleton height="10rem" count={2} style={{ marginBottom: 10 }} />
        </Flex.Item>
      )}
    </Flex>
  );
};

export default PublicQualitativeMeasure;
